/**
 * Helpers shared across WP REST list calls.
 *
 * Every taxonomy/CPT wrapper in the app builds its own query-string
 * with the same `page` / `per_page` / `search` shape. Centralising
 * those rules here keeps the page-size clamp, the trim policy, and
 * the empty-search omission consistent — and makes it a one-file
 * change to e.g. enable `_fields` projection across the board.
 */
import { ApiError } from './errors'

/**
 * The shape every list query in the codebase carries. Concrete list
 * queries (`DishListQuery`, `CategoryListQuery`, etc.) extend this
 * with their own filters; the base fields are handled here.
 */
export interface WpListQuery {
  page?: number
  perPage?: number
  search?: string
}

/**
 * Translate the shared base fields into the WP-shaped query object.
 *
 * - `per_page` clamps to `[1, 100]` — WP's hard maximum; sending more
 *   400s with `rest_post_invalid_page_number`.
 * - `search` is trimmed; an empty/whitespace search is dropped
 *   entirely (otherwise WP runs a "match anything" pass which is
 *   slower than the unfiltered list).
 *
 * @param q       The caller's UI-side query object.
 * @param defaults Per-feature defaults: at minimum the desired
 *                 `perPage` when the caller doesn't set one.
 */
export function buildListParams(
  q: WpListQuery,
  defaults: { perPage: number },
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {
    page: q.page ?? 1,
    per_page: Math.min(Math.max(q.perPage ?? defaults.perPage, 1), 100),
  }
  const search = q.search?.trim()
  if (search) out.search = search
  return out
}

/**
 * Run a taxonomy list that sorts by a custom `orderby` (e.g.
 * `term_order`), retrying WITHOUT the sort if the site's plugin hasn't
 * whitelisted it.
 *
 * Background: the dev plugin whitelists `term_order` on every taxonomy,
 * but some production sites run an older build where `ezcategory`,
 * `eztag` and `ezmodgroup` still reject it — WP answers
 * `400 rest_invalid_param` (`orderby`). Hard-failing there blanks the
 * whole categories/tags/shared-mods screen for those merchants. Falling
 * back to WP's default order (name asc) keeps the screen usable; the
 * manager-set drag order simply isn't reflected until the plugin is
 * updated.
 *
 * Only the `orderby`-specific 400 triggers the retry — every other
 * error (auth, network, a different bad param) propagates unchanged.
 *
 * @param run    Performs the actual `api.list`, given the final params.
 * @param params The full query params INCLUDING `orderby` / `order`.
 */
export async function listWithOrderbyFallback<T>(
  run: (params: Record<string, string | number | boolean>) => Promise<T>,
  params: Record<string, string | number | boolean>,
): Promise<T> {
  try {
    return await run(params)
  } catch (err) {
    if (err instanceof ApiError && err.isInvalidOrderby()) {
      const rest = { ...params }
      delete rest.orderby
      delete rest.order
      return run(rest)
    }
    throw err
  }
}
